import { useCallback, useMemo, useState, useEffect } from "react";
import { Button, Container, Header, Icon, Label, Loader, Menu, Message, Segment } from "semantic-ui-react";
import { useBooking } from "../context/BookingContext";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import useAdminUrlState from "../hooks/useAdminUrlState";
import AvailabilityCalendar from "../components/AvailabilityCalendar";
import { bookingApi } from "../api/api";
import { mapBooking } from "../api/mappers";
import SummaryTab from "../components/admin/SummaryTab";
import BookingsTab from "../components/admin/BookingsTab";
import CreateBookingTab from "../components/admin/CreateBookingTab";
import FrontDeskTab from "../components/admin/FrontDeskTab";
import RoomMapTab from "../components/admin/RoomMapTab";
import GuestHouseTab from "../components/admin/GuestHouseTab";
import RoomOverviewPage from "../components/admin/RoomOverviewPage";
import BookingDetailModal from "../components/admin/BookingDetailModal";
import SendMailModal from "../components/admin/SendMailModal";

/* ── Main Admin Page ─────────────────────────────────── */
function AdminDashboard() {
  const {
    bookings,
    rooms,
    guestHouses,
    roomTypes,
    floorPriorities,
    confirmBooking,
    partialConfirmBooking,
    addRoomToBooking,
    rejectBooking,
    cancelBooking,
    transferBooking,
    rescheduleBooking,
    editBookingDetails,
    adminEditBookingField,
    adminEditRoomField,
    checkinBooking,
    checkoutBooking,
    extendBooking,
    changeRoomBooking,
    removeRoomFromBooking,
    setRoomGuestName,
    addRoom,
    updateRoom,
    deleteRoom,
    addRoomType,
    updateRoomType,
    deleteRoomType,
    addGuestHouse,
    updateGuestHouse,
    deleteGuestHouse,
    updateGuestHouseBookingSettings,
    addFloorPriority,
    updateFloorPriority,
    deleteFloorPriority,
    paymentResponsibilityOptions,
    addPaymentResponsibilityOption,
    updatePaymentResponsibilityOption,
    deletePaymentResponsibilityOption,
    advanceBookingLimits,
    updateAdvanceBookingLimit,
    apiLoading,
    apiError,
    hasLoadedOnce,
    reloadFromApi,
  } = useBooking();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const openBill = useCallback((id) => navigate(`/gh-admin/bill/${id}`), [navigate]);
  const isSuperAdmin = currentUser?.apiRole === "SUPER_ADMIN";
  const canFieldEdit =
    ["SUPER_ADMIN", "GH_ADMIN"].includes(currentUser?.apiRole) &&
    Boolean(currentUser?.hasEditAccess);
  const {
    params,
    setTab,
    openDetail: openDetailUrl,
    openDetailFromRoomMap: openDetailFromRoomMapUrl,
    closeDetail,
    clearRoom,
    toggleCalendar,
    setGhSub,
    openGuestHouseManage,
    openRoomOverview,
    openBookingsFiltered,
    setStat,
    setBookingsFilter,
    setSummaryFilter,
    setFrontDeskFilter,
    setRoomMapView,
    forceTab,
    goHome: goHomeUrl,
  } = useAdminUrlState();
  const activeTab = params.tab;
  const showCalendar = params.calendar;
  /* A saved sub-tab that needs SUPER_ADMIN no longer applies once a
     non-super-admin lands on this URL (a shared browser, a demoted account) —
     fall back the same way the old localStorage restore used to. */
  const ghSubRaw = params.ghSub || (isSuperAdmin ? "guest-house" : "room-types");
  const ghSubTab =
    (ghSubRaw === "guest-house" || ghSubRaw === "admin-users") && !isSuperAdmin
      ? "room-types"
      : ghSubRaw;
  const roomOverviewGh = guestHouses.find((g) => String(g.id) === String(params.gh)) || null;
  /* The indent a "Reuse Details" click seeded the Create Booking tab from.
     In-memory only, deliberately not URL-persisted: a hard refresh lands on
     an empty create form, which is what we want. */
  const [createBookingPrefill, setCreateBookingPrefill] = useState(null);
  const [notice, setNotice] = useState(null);
  /* Set right after confirm/reject/cancel when the backend reports no
     automatic mail went out for that action, so the admin gets asked before
     the moment passes rather than having to remember "Send Mail" by hand. */
  const [mailPromptBooking, setMailPromptBooking] = useState(null);
  const [detailBooking, setDetailBooking] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  /* GET /bookings/ (the bulk list bookings is loaded from) doesn't reliably
     include each booking's room_assignments — only GET /bookings/{id}/ does.
     Without this, the calendar's "occupied rooms" list can't show a room
     number for a booking that was only ever loaded via the list. */
  const [fetchedBookingRooms, setFetchedBookingRooms] = useState({});

  /* A stale room-overview link (guest house deleted, or a bookmark from
     before this account lost access) can't resolve once the guest-house
     list has actually loaded — fall back rather than render a blank tab. */
  useEffect(() => {
    if (hasLoadedOnce && activeTab === "room-overview" && !roomOverviewGh) {
      forceTab("summary");
    }
  }, [hasLoadedOnce, activeTab, roomOverviewGh, forceTab]);

  /* Resolve the `b` query param into the editable, full-detail booking view.
     Two other widgets also open off `b` (BookingsTab's view-only modal under
     `view=1`, FrontDeskTab's own modal) but those resolve locally from
     already-loaded list data — see those components — so they're excluded
     here rather than this effect racing them for the same param. */
  useEffect(() => {
    const id = params.b;
    const isLocalView = (activeTab === "bookings" && params.view) || activeTab === "front-desk";
    if (!hasLoadedOnce || !id || isLocalView) {
      setDetailBooking(null);
      return undefined;
    }
    const listMatch = bookings.find((b) => String(b.id) === String(id));
    let cancelled = false;
    setDetailLoading(true);
    if (listMatch) setDetailBooking(listMatch); /* show immediately with list data while fetching */
    bookingApi
      .get(id)
      .then((full) => {
        if (!cancelled) setDetailBooking(mapBooking(full));
      })
      .catch(() => {
        /* No list-level fallback either (e.g. a refresh landed straight on a
           stale/deleted id) — there's nothing to show, so back out of the
           detail view instead of leaving a permanent loading state. */
        if (!cancelled && !listMatch) closeDetail();
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.b, activeTab, params.view, hasLoadedOnce]);

  /* Header logo/title click — reset back to the Summary tab even when
     already on /gh-admin, since navigating to the same route is a no-op. */
  useEffect(() => {
    const goHome = () => goHomeUrl();
    window.addEventListener("bhu:admin-go-home", goHome);
    return () => window.removeEventListener("bhu:admin-go-home", goHome);
  }, [goHomeUrl]);

  const handleGhClick = (ghId) => {
    openRoomOverview(ghId);
  };

  const handleGhBookedClick = (ghId) => {
    openBookingsFiltered("Approved", ghId);
  };

  /* Fetch full detail (which includes room_assignments) for any confirmed
     booking the calendar can't already resolve a room number for. */
  useEffect(() => {
    if (!showCalendar) return;
    const missing = bookings.filter(
      (b) =>
        (b.status === "Approved" || b.status === "Partially Confirmed") &&
        !b.roomId &&
        !(b.roomAssignments || []).length &&
        !(b.id in fetchedBookingRooms),
    );
    if (!missing.length) return;

    let cancelled = false;
    missing.forEach((b) => {
      bookingApi
        .get(b.id)
        .then((full) => {
          if (cancelled) return;
          const mapped = mapBooking(full);
          setFetchedBookingRooms((prev) => ({
            ...prev,
            [b.id]: mapped.roomAssignments || [],
          }));
        })
        .catch(() => {
          if (!cancelled) {
            setFetchedBookingRooms((prev) => ({ ...prev, [b.id]: [] }));
          }
        });
    });
    return () => {
      cancelled = true;
    };
  }, [showCalendar, bookings, fetchedBookingRooms]);

  /* While any of those detail fetches are still in flight, the calendar
     would otherwise render with the "N room(s)" placeholder first and then
     flip to the real room number a moment later. Hold off rendering it
     until everything has resolved so it appears fully-formed in one go. */
  const pendingCalendarRoomFetches = useMemo(
    () =>
      bookings.some(
        (b) =>
          (b.status === "Approved" || b.status === "Partially Confirmed") &&
          !b.roomId &&
          !(b.roomAssignments || []).length &&
          !(b.id in fetchedBookingRooms),
      ),
    [bookings, fetchedBookingRooms],
  );

  const calendarData = useMemo(
    () => ({
      guestHouses: guestHouses.map((gh) => ({
        name: gh.name,
        rooms: rooms
          .filter((r) => Number(r.guestHouseId) === Number(gh.id))
          .map((r) => ({
            roomNumber: r.roomNumber,
            roomType: r.roomType || "",
            status: r.status === "maintenance" ? "Maintenance" : "Available",
          })),
        requests: bookings
          .filter((b) => Number(b.guestHouseId) === Number(gh.id))
          .map((b) => {
            const primaryRoom = rooms.find(
              (r) => Number(r.id) === Number(b.roomId),
            );
            const assignedRooms = (
              b.roomAssignments?.length
                ? b.roomAssignments
                : fetchedBookingRooms[b.id] || []
            )
              .filter((a) => a.roomNumber)
              .map((a) => ({
                roomNumber: a.roomNumber,
                roomType: a.roomType || "",
              }));
            if (!assignedRooms.length && primaryRoom) {
              assignedRooms.push({
                roomNumber: primaryRoom.roomNumber,
                roomType: primaryRoom.roomType || "",
              });
            }
            return {
              requestId: b.bookingCode,
              status: b.status,
              checkIn: b.checkIn,
              checkOut: b.checkOut,
              roomsRequired: Number(b.guests || b.roomCount || 1),
              assignedRoom: primaryRoom?.roomNumber || "",
              assignedRooms,
              applicantName: b.applicantName || b.userName || "",
              guestName: b.guestName || "",
            };
          }),
      })),
    }),
    [guestHouses, rooms, bookings, fetchedBookingRooms],
  );

  /* Notices stay up until the admin closes them: several name a booking or a
     room the admin needs while they act on it, and an auto-dismissing one is
     gone before it can be read. */
  const showNotice = (msg, type = "success") => setNotice({ msg, type });

  const showActionError = (e, fallback) =>
    showNotice(e.message || fallback, "warning");

  const handleApprove = async (id, roomIds, remarks = "", roomWindows) => {
    try {
      const updated = await confirmBooking(id, roomIds, remarks, roomWindows);
      showNotice("Booking approved and room assigned.");
      if (updated.mailAutoSent === false) {
        setMailPromptBooking({ id, empId: updated.employeeId });
      }
      reloadFromApi();
    } catch (e) {
      showActionError(e, "Failed to confirm booking.");
    }
  };

  const handleReject = async (id, remarks = "") => {
    try {
      const updated = await rejectBooking(id, remarks);
      showNotice("Booking rejected.", "warning");
      // Rejection never auto-mails (backend always reports mailAutoSent:
      // false for it) — the admin reviews/edits the rejection mail here
      // instead of it going out unattended.
      if (updated.mailAutoSent === false) {
        setMailPromptBooking({
          id,
          empId: updated.employeeId,
          heading: "Send the rejection mail",
          helperText: "This booking was rejected. Review the mail below before sending it to the applicant.",
          defaultTemplate: "NON_AVAILABILITY",
        });
      }
      reloadFromApi();
    } catch (e) {
      showNotice(e.message || "Failed to reject booking.", "warning");
    }
  };

  /* Unlike the other actions this rethrows: the Send Mail dialog stays open on
     failure so the admin can read the mail service's own message and retry,
     rather than the panel closing on them. */
  const handleSendMail = async (id, template, amount) => {
    try {
      await bookingApi.sendMail(id, template, amount);
      showNotice("Email sent to the applicant.", "success");
    } catch (e) {
      showNotice(e.message || "The email was not sent.", "warning");
      throw e;
    }
  };

  const handlePartialConfirm = async (id, roomIds, note = "") => {
    try {
      await partialConfirmBooking(id, roomIds, note);
      showNotice("Booking partially confirmed.");
      reloadFromApi();
    } catch (e) {
      showActionError(e, "Failed to partially confirm booking.");
    }
  };

  const handleAddRoomToBooking = async (id, roomIds, roomWindows) => {
    try {
      const updated = await addRoomToBooking(id, roomIds, roomWindows);
      /* Keep the open detail modal in sync — otherwise it still shows the
         pre-add-room list until closed and reopened. */
      setDetailBooking((current) =>
        current?.id === id ? updated : current,
      );
      showNotice(
        roomIds.length > 1 ? "Rooms added successfully." : "Room added successfully.",
      );
      reloadFromApi();
    } catch (e) {
      showActionError(e, "Failed to add room(s).");
    }
  };

  const handleTransfer = async (id, toGhId, remarks = "") => {
    try {
      const updated = await transferBooking(id, toGhId, remarks);
      /* Keep the open detail modal in sync — otherwise it still shows the
         pre-transfer guest house/room data until closed and reopened. */
      setDetailBooking((current) =>
        current?.id === id ? updated : current,
      );
      showNotice("Booking transferred successfully.");
      reloadFromApi();
    } catch (e) {
      showNotice(e.message || "Failed to transfer booking.", "warning");
    }
  };

  const handleReschedule = async (id, payload) => {
    try {
      const updated = await rescheduleBooking(id, payload);
      setDetailBooking((current) =>
        current?.id === id ? updated : current,
      );
      showNotice("Booking rescheduled successfully.");
      reloadFromApi();
    } catch (e) {
      showNotice(e.message || "Failed to reschedule booking.", "warning");
    }
  };

  const handleEditDetails = async (id, payload) => {
    try {
      const updated = await editBookingDetails(id, payload);
      setDetailBooking((current) =>
        current?.id === id ? updated : current,
      );
      showNotice("Booking details updated successfully.");
      reloadFromApi();
    } catch (e) {
      showNotice(e.message || "Failed to update booking details.", "warning");
    }
  };

  const handleFieldEdit = async (id, payload) => {
    try {
      const updated = await adminEditBookingField(id, payload);
      setDetailBooking((current) =>
        current?.id === id ? updated : current,
      );
      showNotice("Field updated.");
      reloadFromApi();
    } catch (e) {
      showNotice(e.message || "Failed to update field.", "warning");
      // Re-throw so the inline editor keeps its popup open and shows the error.
      throw e;
    }
  };

  const handleRoomFieldEdit = async (id, payload) => {
    try {
      const updated = await adminEditRoomField(id, payload);
      setDetailBooking((current) =>
        current?.id === id ? updated : current,
      );
      showNotice("Field updated.");
      reloadFromApi();
    } catch (e) {
      showNotice(e.message || "Failed to update field.", "warning");
      throw e;
    }
  };

  const handleCheckin = async (id, assignmentIds, noShow = false) => {
    try {
      const updated = await checkinBooking(id, assignmentIds, noShow);
      setDetailBooking((current) =>
        current?.id === id ? updated : current,
      );
      showNotice(noShow ? "Room(s) marked as no-show." : "Checked in successfully.");
      reloadFromApi();
    } catch (e) {
      showNotice(e.message || "Failed to check in.", "warning");
    }
  };

  const handleCheckout = async (id, assignmentIds) => {
    try {
      const updated = await checkoutBooking(id, assignmentIds);
      setDetailBooking((current) =>
        current?.id === id ? updated : current,
      );
      showNotice("Checked out successfully.");
      reloadFromApi();
    } catch (e) {
      showNotice(e.message || "Failed to check out.", "warning");
    }
  };

  const handleExtend = async (id, newDepartureDate, assignmentIds, newDepartureTime, arrival) => {
    try {
      const updated = await extendBooking(id, newDepartureDate, assignmentIds, newDepartureTime, arrival);
      setDetailBooking((current) =>
        current?.id === id ? updated : current,
      );
      showNotice("Departure updated.");
      reloadFromApi();
    } catch (e) {
      showNotice(e.message || "Failed to change the departure.", "warning");
      /* Re-thrown so the room's dialog stays open and shows why. */
      throw e;
    }
  };

  const handleChangeRoom = async (id, assignmentId, newRoomId, oldRoomId) => {
    try {
      const updated = await changeRoomBooking(id, assignmentId, newRoomId, oldRoomId);
      setDetailBooking((current) =>
        current?.id === id ? updated : current,
      );
      showNotice("Room changed successfully.");
      reloadFromApi();
    } catch (e) {
      showActionError(e, "Failed to change room.");
    }
  };

  const handleRemoveRoom = async (id, assignmentId, remarks) => {
    try {
      const updated = await removeRoomFromBooking(id, assignmentId, remarks);
      setDetailBooking((current) =>
        current?.id === id ? updated : current,
      );
      showNotice(
        updated._apiStatus === "CANCELLED"
          ? "Last room removed — booking cancelled."
          : "Room removed from booking.",
      );
      reloadFromApi();
    } catch (e) {
      showActionError(e, "Failed to remove room.");
      throw e;
    }
  };

  const handleSetRoomGuestName = async (id, assignmentId, guestName) => {
    try {
      const updated = await setRoomGuestName(id, assignmentId, guestName);
      setDetailBooking((current) =>
        current?.id === id ? updated : current,
      );
      showNotice(guestName ? "Guest name updated." : "Guest name cleared.");
      reloadFromApi();
    } catch (e) {
      showNotice(e.message || "Failed to update guest name.", "warning");
    }
  };

  const handleCancel = async (id, remarks = "") => {
    try {
      const updated = await cancelBooking(id, remarks);
      showNotice("Booking cancelled.", "warning");
      if (updated?.mailAutoSent === false) {
        setMailPromptBooking({ id, empId: updated.employeeId });
      }
      reloadFromApi();
    } catch (e) {
      showNotice(e.message || "Failed to cancel booking.", "warning");
    }
  };

  const handleAddRoom = async (data) => {
    try {
      await addRoom(data);
      showNotice("Room added successfully.");
    } catch (e) {
      showNotice(e.message || "Failed to add room.", "warning");
    }
  };

  const handleUpdateRoom = async (id, data) => {
    try {
      await updateRoom(id, data);
    } catch (e) {
      showNotice(e.message || "Failed to update room.", "warning");
    }
  };

  const handleDeleteRoom = async (id) => {
    try {
      await deleteRoom(id);
      showNotice("Room deleted.");
    } catch (e) {
      showNotice(e.message || "Failed to delete room.", "warning");
      throw e;
    }
  };

  const handleUpdateGuestHouse = async (id, data) => {
    try {
      await updateGuestHouse(id, data);
      showNotice("Guest house updated.");
    } catch (e) {
      showNotice(e.message || "Failed to update guest house.", "warning");
    }
  };

  const handleDeleteGuestHouse = async (id) => {
    try {
      await deleteGuestHouse(id);
      showNotice("Guest house deleted.");
    } catch (e) {
      showNotice(e.message || "Failed to delete guest house.", "warning");
      throw e;
    }
  };

  const handleUpdateRoomType = async (ghId, roomTypeId, data) => {
    try {
      await updateRoomType(ghId, roomTypeId, data);
      showNotice("Room type updated.");
    } catch (e) {
      showNotice(e.message || "Failed to update room type.", "warning");
    }
  };

  /* Raise a new indent carrying this one's applicant/guest details over.
     Create Booking is a tab, not a route, so the seed travels as state. */
  const handleReuseDetails = (booking) => {
    setCreateBookingPrefill(booking);
    setTab("create-booking");
  };

  /* Opening a booking just sets `b` in the URL — the effect above does the
     actual fetch, whether that's this click (list data already known, below)
     or a page refresh landing straight on the id. */
  const openDetail = (b) => openDetailUrl(b.id);

  /* Opened from the Room Map popup — carries the room/guest house/date along
     so RoomMapTab's initialSelection effect can re-open that same popup on
     Back (see RoomMapTab.js). */
  const openDetailFromRoomMap = (b, ctx) => {
    openDetailFromRoomMapUrl(b.id, { roomId: ctx?.roomId, gh: ctx?.ghId, date: ctx?.mapDate });
  };

  const tabs = [
    { key: "summary", label: "Dashboard", icon: "chart bar outline" },
    { key: "create-booking", label: "Create Booking", icon: "plus circle" },
    { key: "front-desk", label: "Front Desk", icon: "bed" },
    { key: "room-map", label: "Room Map", icon: "th" },
    { key: "guest-house", label: "Manage", icon: "building" },
  ];
  const showBackButton = activeTab !== "summary" && activeTab !== "room-overview";

  return (
    <Container fluid>
      {apiError && !apiLoading && (
        <Message warning className="adm-notice">
          <Icon name="exclamation triangle" />
          Server unavailable — showing cached data. ({apiError})
        </Message>
      )}
      {notice && (
        <Message
          positive={notice.type === "success"}
          warning={notice.type === "warning"}
          className="adm-notice"
          onDismiss={() => setNotice(null)}
        >
          <Icon
            name={
              notice.type === "success"
                ? "check circle"
                : "exclamation triangle"
            }
          />
          {notice.msg}
        </Message>
      )}

      <SendMailModal
        open={!!mailPromptBooking}
        bookingId={mailPromptBooking?.id}
        empId={mailPromptBooking?.empId}
        heading={mailPromptBooking?.heading || "Send a mail?"}
        helperText={mailPromptBooking?.helperText || "No automatic email was sent for this action."}
        defaultTemplate={mailPromptBooking?.defaultTemplate || ""}
        onSend={handleSendMail}
        onClose={() => setMailPromptBooking(null)}
        onSent={() => {
          setMailPromptBooking(null);
          reloadFromApi();
        }}
      />

      {activeTab !== "guest-house" && (
        <Menu
          pointing
          secondary
          size="small"
          className="adm-tabs"
          style={{ marginTop: 0, marginBottom: 6 }}
        >
          {tabs
            .filter((t) => t.key !== "guest-house")
            .map((t) => (
              <Menu.Item
                key={t.key}
                name={t.key}
                active={activeTab === t.key && !showCalendar && !detailBooking}
                onClick={() => {
                  /* Only the Reuse Details path seeds the form — reaching the
                     tab through the menu always starts blank. */
                  setCreateBookingPrefill(null);
                  setTab(t.key);
                }}
              >
                <Icon name={t.icon} />
                {t.label}
                {t.badge > 0 && (
                  <Label circular color="red" size="mini">
                    {t.badge}
                  </Label>
                )}
              </Menu.Item>
            ))}
          <Menu.Menu position="right">
            <Menu.Item
              name="guest-house"
              onClick={() => setTab("guest-house")}
            >
              <Icon name="building" />
              Manage
            </Menu.Item>
            <Menu.Item
              name="calendar"
              active={showCalendar}
              onClick={toggleCalendar}
            >
              <Icon name="calendar alternate outline" />
              Availability Calendar
            </Menu.Item>
          </Menu.Menu>
        </Menu>
      )}

      {!hasLoadedOnce ? (
        <Segment basic>
          <Loader active inline="centered" size="large">
            Loading dashboard…
          </Loader>
        </Segment>
      ) : detailBooking ? (
        <>
          {detailLoading && (
            <Message info className="adm-notice" style={{ marginBottom: 0 }}>
              <Icon name="spinner" loading />
              Fetching full booking details…
            </Message>
          )}
          <BookingDetailModal
            onOpenBill={openBill}
            booking={detailBooking}
            rooms={rooms}
            guestHouses={guestHouses}
            roomTypes={roomTypes}
            paymentOptions={paymentResponsibilityOptions}
            onClose={closeDetail}
            onApprove={handleApprove}
            onReject={handleReject}
            onPartialConfirm={handlePartialConfirm}
            onAddRoom={handleAddRoomToBooking}
            onTransfer={handleTransfer}
            onCancel={handleCancel}
            onReschedule={handleReschedule}
            onEditDetails={handleEditDetails}
            canFieldEdit={canFieldEdit}
            onFieldEdit={handleFieldEdit}
            onRoomFieldEdit={handleRoomFieldEdit}
            onCheckin={handleCheckin}
            onCheckout={handleCheckout}
            onExtend={handleExtend}
            onChangeRoom={handleChangeRoom}
            onRemoveRoom={handleRemoveRoom}
            onSetRoomGuestName={handleSetRoomGuestName}
            onReuseDetails={handleReuseDetails}
            onSendMail={handleSendMail}
          />
        </>
      ) : showCalendar ? (
        <div style={{ marginTop: 4 }}>
          {pendingCalendarRoomFetches ? (
            <Segment basic>
              <Loader active inline="centered" size="large">
                Loading room details…
              </Loader>
            </Segment>
          ) : (
            <AvailabilityCalendar bookingData={calendarData} />
          )}
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "flex-start", gap: showBackButton ? 12 : 0 }}>
          {/* Dropped from the flow entirely on the tabs with no "back" target,
             rather than left in place hidden: reserving its width there
             indented the content column ~52px on the left while the right
             edge stayed put, so the Dashboard table sat visibly off-centre
             and out of line with the tab bar above it. `showBackButton`
             tracks nothing but `activeTab`, so the column can only shift on
             a tab switch — which repaints the whole panel anyway — never
             while the user is sitting on one tab. */}
          {showBackButton && (
            <Button
              icon
              circular
              basic
              onClick={() => setTab("summary")}
              title="Back"
              style={{ flexShrink: 0 }}
            >
              <Icon name="arrow left" />
            </Button>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            {activeTab === "summary" && (
              <SummaryTab
                isSuperAdmin={isSuperAdmin}
                bookings={bookings}
                rooms={rooms}
                guestHouses={guestHouses}
                onGhClick={handleGhClick}
                onGhBookedClick={handleGhBookedClick}
                onOpenDetail={openDetail}
                activeKey={params.stat}
                onActiveKeyChange={setStat}
                month={params.month}
                bookingTypeFilter={params.btype}
                dayFilter={params.day}
                colArrival={params.colArrival}
                colDeparture={params.colDeparture}
                searchQuery={params.sQuery}
                onFilterChange={setSummaryFilter}
              />
            )}
            {activeTab === "bookings" && (
              <BookingsTab
                rooms={rooms}
                guestHouses={guestHouses}
                onOpenDetail={openDetail}
                onOpenView={(b) => openDetailUrl(b.id, { view: true })}
                onCloseView={closeDetail}
                openBookingId={params.view ? params.b : null}
                statusFilter={params.status}
                ghFilter={params.gh}
                checkInFrom={params.from}
                checkInTo={params.to}
                searchQuery={params.q}
                onFilterChange={setBookingsFilter}
                viewMode={params.view}
              />
            )}
            {activeTab === "create-booking" && (
              <CreateBookingTab
                /* Remounts between the seeded and the blank form. Clicking
                   "Create Booking" in the menu while the tab is already open
                   only nulls the prefill — without a key change the component
                   would stay mounted and keep the values it was seeded with. */
                key={createBookingPrefill ? `reuse-${createBookingPrefill.id}` : "blank"}
                guestHouses={guestHouses}
                paymentResponsibilityOptions={paymentResponsibilityOptions}
                isSuperAdmin={isSuperAdmin}
                prefillBooking={createBookingPrefill}
                onSuccess={() => {
                  reloadFromApi();
                  setCreateBookingPrefill(null);
                  setTab("summary");
                }}
              />
            )}
            {activeTab === "front-desk" && (
              <FrontDeskTab
                guestHouses={guestHouses}
                onOpenBill={openBill}
                ghFilter={params.gh}
                searchQuery={params.fdQuery}
                onFilterChange={setFrontDeskFilter}
                openBookingId={params.b}
                onOpenDetail={(id) => openDetailUrl(id)}
                onCloseDetail={closeDetail}
              />
            )}
            {activeTab === "room-map" && (
              <RoomMapTab
                guestHouses={guestHouses}
                onOpenBill={openBill}
                onOpenDetail={openDetailFromRoomMap}
                ghId={params.gh}
                mapDate={params.date}
                onViewChange={setRoomMapView}
                initialRoomId={params.room}
                onInitialSelectionConsumed={clearRoom}
              />
            )}
            {activeTab === "guest-house" && (
              <GuestHouseTab
                rooms={rooms}
                guestHouses={guestHouses}
                roomTypes={roomTypes}
                floorPriorities={floorPriorities}
                onAddGuestHouse={addGuestHouse}
                onUpdateGuestHouse={handleUpdateGuestHouse}
                onDeleteGuestHouse={handleDeleteGuestHouse}
                onUpdateGuestHouseBookingSettings={updateGuestHouseBookingSettings}
                onAddRoom={handleAddRoom}
                onUpdateRoom={handleUpdateRoom}
                onDeleteRoom={handleDeleteRoom}
                onAddRoomType={addRoomType}
                onUpdateRoomType={handleUpdateRoomType}
                onDeleteRoomType={deleteRoomType}
                onAddFloorPriority={addFloorPriority}
                onUpdateFloorPriority={updateFloorPriority}
                onDeleteFloorPriority={deleteFloorPriority}
                paymentResponsibilityOptions={paymentResponsibilityOptions}
                onAddPaymentResponsibilityOption={addPaymentResponsibilityOption}
                onUpdatePaymentResponsibilityOption={updatePaymentResponsibilityOption}
                onDeletePaymentResponsibilityOption={deletePaymentResponsibilityOption}
                advanceBookingLimits={advanceBookingLimits}
                onUpdateAdvanceBookingLimit={updateAdvanceBookingLimit}
                subTab={ghSubTab}
                onSubTabChange={setGhSub}
                isSuperAdmin={isSuperAdmin}
              />
            )}
            {activeTab === "room-overview" && roomOverviewGh && (
              <RoomOverviewPage
                gh={roomOverviewGh}
                rooms={rooms}
                bookings={bookings}
                onBack={() => setTab("summary")}
                onManage={() => openGuestHouseManage("rooms")}
              />
            )}
          </div>
        </div>
      )}
    </Container>
  );
}

export default AdminDashboard;
